import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  FlaskConical, Plus, Trash2, Pause, Play, BarChart3, Trophy, RefreshCw, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

/**
 * Admin A/B Experiments page.
 *
 * Create split-tests across existing promo codes. Deterministic hash
 * bucketing keeps variant assignments stable per user, so repeat visits
 * don't pollute conversion data.
 */
export default function ExperimentsPage() {
  const [experiments, setExperiments] = useState([]);
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedResults, setSelectedResults] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [expRes, promoRes] = await Promise.all([
        api.get('/api/admin/experiments'),
        api.get('/api/admin/promotions?limit=100'),
      ]);
      setExperiments(expRes.data?.experiments || []);
      setPromos(promoRes.data?.items || promoRes.data?.promotions || []);
    } catch {
      toast.error('Failed to load experiments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleStatus = async (exp) => {
    const next = exp.status === 'running' ? 'paused' : 'running';
    try {
      await api.patch(`/api/admin/experiments/${exp.id}`, { status: next });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const complete = async (exp) => {
    if (!window.confirm(`Complete "${exp.name}"? Further assignments will be blocked.`)) return;
    try {
      await api.patch(`/api/admin/experiments/${exp.id}`, { status: 'completed' });
      toast.success('Experiment completed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (exp) => {
    if (!window.confirm(`Delete "${exp.name}" and all its data?`)) return;
    try {
      await api.delete(`/api/admin/experiments/${exp.id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="experiments-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-[#D4AF37]" /> A/B Experiments
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Split-test promo variants across real users and let conversion rate pick the winner.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-[#D4AF37] hover:bg-[#c39d2f] text-black font-bold"
          data-testid="experiments-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New experiment
        </Button>
      </div>

      <div className="bg-[#131822] border border-white/5 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#D4AF37]" />
            <h2 className="text-sm font-semibold text-white">All experiments</h2>
            <span className="text-[11px] text-gray-500">{experiments.length}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="gap-1" data-testid="experiments-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {experiments.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-gray-500">
            No experiments yet. Create one above — pick 2+ promos as variants and start testing.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {experiments.map((e) => (
              <ExperimentRow
                key={e.id}
                exp={e}
                onToggle={() => toggleStatus(e)}
                onComplete={() => complete(e)}
                onRemove={() => remove(e)}
                onViewResults={() => setSelectedResults(e)}
              />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateExperimentModal
          promos={promos}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {selectedResults && (
        <ResultsModal
          exp={selectedResults}
          onClose={() => setSelectedResults(null)}
        />
      )}
    </div>
  );
}

function ExperimentRow({ exp, onToggle, onComplete, onRemove, onViewResults }) {
  const statusColors = {
    running: 'bg-emerald-500/15 text-emerald-400',
    paused: 'bg-[#D4AF37]/15 text-[#D4AF37]',
    completed: 'bg-gray-500/15 text-gray-400',
  };
  return (
    <div className="px-4 py-3 grid grid-cols-12 gap-3 items-center text-sm" data-testid={`experiment-row-${exp.id}`}>
      <div className="col-span-4 min-w-0">
        <div className="text-white font-medium truncate">{exp.name}</div>
        <div className="text-[11px] text-gray-500 truncate">{exp.description || '—'}</div>
      </div>
      <div className="col-span-3 text-[11px] text-gray-400">
        {(exp.variants || []).map((v) => (
          <span key={v.id} className="inline-block mr-2">
            <b className="text-white">{v.name}</b>
            <span className="text-gray-500"> · {v.promo_code}</span>
          </span>
        ))}
      </div>
      <div className="col-span-2">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[exp.status] || ''}`}>
          {exp.status}
        </span>
      </div>
      <div className="col-span-3 flex justify-end gap-1.5">
        <Button size="sm" variant="outline" onClick={onViewResults} className="border-white/10 h-7 text-[11px] gap-1" data-testid={`experiment-results-${exp.id}`}>
          <BarChart3 className="w-3 h-3" /> Results
        </Button>
        {exp.status !== 'completed' && (
          <>
            <Button size="sm" variant="outline" onClick={onToggle} className="border-white/10 h-7 text-[11px] gap-1" data-testid={`experiment-toggle-${exp.id}`}>
              {exp.status === 'running' ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Resume</>}
            </Button>
            <Button size="sm" variant="outline" onClick={onComplete} className="border-white/10 h-7 text-[11px] gap-1" data-testid={`experiment-complete-${exp.id}`}>
              <Trophy className="w-3 h-3" /> Complete
            </Button>
          </>
        )}
        <button onClick={onRemove} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400" title="Delete" data-testid={`experiment-delete-${exp.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function CreateExperimentModal({ promos, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [variants, setVariants] = useState([
    { name: 'A', promo_id: '', weight: 1 },
    { name: 'B', promo_id: '', weight: 1 },
  ]);
  const [saving, setSaving] = useState(false);

  const addVariant = () => {
    if (variants.length >= 6) return;
    const letter = String.fromCharCode('A'.charCodeAt(0) + variants.length);
    setVariants([...variants, { name: letter, promo_id: '', weight: 1 }]);
  };

  const removeVariant = (idx) => {
    if (variants.length <= 2) return;
    setVariants(variants.filter((_, i) => i !== idx));
  };

  const setVariant = (idx, patch) => {
    setVariants(variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  const submit = async () => {
    if (!name.trim() || name.trim().length < 3) {
      toast.error('Name must be at least 3 characters');
      return;
    }
    if (variants.some((v) => !v.promo_id)) {
      toast.error('Every variant needs a promo selected');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/admin/experiments', {
        name: name.trim(),
        description: description.trim() || null,
        variants: variants.map((v) => ({
          name: v.name,
          promo_id: v.promo_id,
          weight: Number(v.weight) || 1,
        })),
      });
      toast.success('Experiment created');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="create-experiment-modal">
      <div className="relative w-full max-w-2xl bg-[#131822] border border-[#D4AF37]/20 rounded-2xl overflow-hidden">
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-black/40 hover:bg-black/70 text-gray-300 hover:text-white" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
        <div className="px-5 pt-5 pb-4 border-b border-white/5">
          <h2 className="text-lg font-bold text-white">Create A/B Experiment</h2>
          <p className="text-[12px] text-gray-400 mt-1">Pick 2+ promos as variants. Conversion rate picks the winner.</p>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-[11px] uppercase text-gray-500 mb-1">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Welcome bonus — 5 FP vs 10 FP" className="bg-[#0B0E14] border-white/10" data-testid="create-experiment-name" />
          </div>
          <div>
            <label className="block text-[11px] uppercase text-gray-500 mb-1">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional hypothesis / notes" className="bg-[#0B0E14] border-white/10" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase text-gray-500">Variants ({variants.length})</label>
              {variants.length < 6 && (
                <button onClick={addVariant} className="text-[11px] text-[#D4AF37] hover:underline">+ Add variant</button>
              )}
            </div>
            <div className="space-y-2">
              {variants.map((v, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <Input
                    className="col-span-2 bg-[#0B0E14] border-white/10"
                    value={v.name}
                    onChange={(e) => setVariant(i, { name: e.target.value })}
                    placeholder="A"
                    data-testid={`variant-name-${i}`}
                  />
                  <select
                    className="col-span-7 bg-[#0B0E14] border border-white/10 rounded-md px-3 text-sm text-white h-10"
                    value={v.promo_id}
                    onChange={(e) => setVariant(i, { promo_id: e.target.value })}
                    data-testid={`variant-promo-${i}`}
                  >
                    <option value="">— pick a promo —</option>
                    {promos.map((p) => (
                      <option key={p.id} value={p.id}>{p.code} · {p.title || p.type}</option>
                    ))}
                  </select>
                  <Input
                    type="number"
                    min={0.1}
                    step={0.1}
                    className="col-span-2 bg-[#0B0E14] border-white/10"
                    value={v.weight}
                    onChange={(e) => setVariant(i, { weight: e.target.value })}
                    data-testid={`variant-weight-${i}`}
                  />
                  <div className="col-span-1 flex items-center justify-center">
                    {variants.length > 2 && (
                      <button onClick={() => removeVariant(i)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400" title="Remove variant">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-white/5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#D4AF37] hover:bg-[#c39d2f] text-black font-bold" data-testid="create-experiment-submit">
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ResultsModal({ exp, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/admin/experiments/${exp.id}/results`);
        if (alive) setData(res.data);
      } catch {
        if (alive) toast.error('Failed to load results');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [exp.id]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="experiment-results-modal">
      <div className="relative w-full max-w-2xl bg-[#131822] border border-[#D4AF37]/20 rounded-2xl overflow-hidden">
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-black/40 hover:bg-black/70 text-gray-300 hover:text-white" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
        <div className="px-5 pt-5 pb-4 border-b border-white/5">
          <h2 className="text-lg font-bold text-white">Results — {exp.name}</h2>
          <p className="text-[12px] text-gray-400 mt-1">Winner needs ≥30 assignments per variant to call.</p>
        </div>
        <div className="px-5 py-4">
          {loading && <div className="text-center text-sm text-gray-500 py-8">Loading…</div>}
          {!loading && data && (
            <>
              {data.winner_variant_id ? (
                <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-300 flex items-center gap-2">
                  <Trophy className="w-4 h-4" /> Winner so far: <b className="text-white">{(data.variants.find((v) => v.id === data.winner_variant_id) || {}).name}</b>
                </div>
              ) : (
                <div className="mb-4 p-3 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-sm text-[#D4AF37]">
                  Gathering data — need at least 30 assignments per variant to call a winner.
                </div>
              )}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase text-gray-500 border-b border-white/5">
                    <th className="text-left py-2">Variant</th>
                    <th className="text-left py-2">Promo</th>
                    <th className="text-right py-2">Assigned</th>
                    <th className="text-right py-2">Converted</th>
                    <th className="text-right py-2">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.variants.map((v) => (
                    <tr key={v.id} className="border-b border-white/5" data-testid={`result-variant-row-${v.id}`}>
                      <td className="py-2 text-white font-medium">{v.name}</td>
                      <td className="py-2 text-xs text-gray-400">{v.promo_code}</td>
                      <td className="py-2 text-right tabular-nums">{v.assigned.toLocaleString()}</td>
                      <td className="py-2 text-right tabular-nums text-emerald-400">{v.converted.toLocaleString()}</td>
                      <td className="py-2 text-right tabular-nums font-semibold">{v.conversion_rate_pct.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
